/* <kit-sticky-cta>
   Mirror of Ignite's standard sticky CTA visual, wired to the kit picker's selected tier.
   - On first paint, populates with the kit picker's default tier label + price.
   - Listens to `kit-picker:tier-change` (bubbles) to update title + price.
   - Listens to `kit-picker:price-change` (synthetic, dispatched on variant change) for variant flips.
   - Clicking the CTA scrolls smoothly to the kit picker, then triggers its submit so behaviour
     matches what the customer expects from a sticky bar (no separate code path for ATC).
   - Hidden until the kit picker has been scrolled past (above the viewport), on every viewport.
   - Honours prefers-reduced-motion: removes the slide-in.
*/
if (!customElements.get('kit-sticky-cta')) {
  class KitStickyCTA extends HTMLElement {
    constructor() {
      super();
      this.titleEl = null;
      this.priceEl = null;
      this.labelEl = null;
      this.labelPriceEl = null;
      this.button = null;
      this.kitPicker = null;
      this.observer = null;
      this.bound = false;
    }

    connectedCallback() {
      this.titleEl = this.querySelector('[data-kit-sticky-title]');
      this.priceEl = this.querySelector('[data-kit-sticky-price]');
      this.labelEl = this.querySelector('[data-kit-sticky-label]');
      this.labelPriceEl = this.querySelector('[data-kit-sticky-label-price]');
      this.button = this.querySelector('[data-kit-sticky-cta]');
      this.kitPicker = document.querySelector('kit-picker');

      if (!this.kitPicker) {
        this.remove();
        return;
      }

      this.bind();
      this.refresh();
      this.setupVisibility();
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      if (this.bound) {
        if (this.button && this._onClickBound) this.button.removeEventListener('click', this._onClickBound);
        if (this._onTierChangeBound) {
          document.removeEventListener('kit-picker:tier-change', this._onTierChangeBound);
          document.removeEventListener('kit-picker:price-change', this._onTierChangeBound);
        }
      }
      this.bound = false;
    }

    bind() {
      // Bind handlers ONCE so add and remove use the same reference (prevents
      // listener leaks on theme-editor reloads).
      if (!this._onClickBound) this._onClickBound = this.onClick.bind(this);
      if (!this._onTierChangeBound) this._onTierChangeBound = this.onTierChange.bind(this);
      this.button.addEventListener('click', this._onClickBound);
      document.addEventListener('kit-picker:tier-change', this._onTierChangeBound);
      document.addEventListener('kit-picker:price-change', this._onTierChangeBound);
      this.bound = true;
    }

    onClick(evt) {
      evt.preventDefault();
      const kit = this.kitPicker;
      const submit = kit.querySelector('[data-kit-submit]');
      const target = kit.getBoundingClientRect();
      const headerOffset = 80;
      const top = window.scrollY + target.top - headerOffset;
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      window.scrollTo({ top: Math.max(0, top), behavior: reduced ? 'instant' : 'smooth' });
      if (!submit) return;
      if (reduced) { submit.click(); return; }

      // Wait for the smooth-scroll to land before triggering the submit so the
      // cart drawer doesn't open while the page is still mid-scroll. Use the
      // scrollend event where supported (Chrome 114+, Safari 18+, Firefox 109+);
      // fall back to a 600ms timeout for older browsers and the case where no
      // scroll was actually needed (no scrollend fires when distance is zero).
      let fired = false;
      const trigger = () => {
        if (fired) return;
        fired = true;
        submit.click();
      };
      window.addEventListener('scrollend', trigger, { once: true });
      setTimeout(trigger, 600);
    }

    onTierChange() {
      this.refresh();
    }

    refresh() {
      const kit = this.kitPicker;
      if (!kit) return;
      const selectedTier = kit.querySelector('.kit-tier--selected');
      if (!selectedTier) return;

      const labelEl = selectedTier.querySelector('.kit-tier__label');
      const priceEl = selectedTier.querySelector('[data-kit-price]');
      const tierLabel = labelEl ? labelEl.textContent.trim() : 'Kit';
      const tierPrice = priceEl ? priceEl.textContent.trim() : '';

      if (this.titleEl) this.titleEl.textContent = tierLabel;
      if (this.priceEl) this.priceEl.textContent = tierPrice;
      if (this.labelEl) this.labelEl.textContent = `Add ${tierLabel}`;
      if (this.labelPriceEl) this.labelPriceEl.textContent = tierPrice ? `, ${tierPrice}` : '';
    }

    setupVisibility() {
      // Hide on first paint until IO confirms the kit picker is fully out of view.
      // The theme's .quick-add-hidden rule is scoped to <quick-add-modal> so it does
      // not match here; kit-sticky-cta.quick-add-hidden in build-the-kit.css covers us.
      this.classList.add('quick-add-hidden');

      if (!('IntersectionObserver' in window)) {
        // Fall back to always-hidden rather than always-visible (avoids overlay bug).
        return;
      }
      // rootMargin -80px on top accounts for the sticky header so the bar appears
      // only when the kit picker is well past the visible area, not at the seam.
      this.observer = new IntersectionObserver((entries) => {
        const entry = entries[0];
        if (!entry) return;
        // The root runs far below the screen, so only a picker scrolled up past the header leaves it,
        // and an instant jump back to the top still re-hides the bar before it can cover the size pills.
        this.classList.toggle('quick-add-hidden', entry.isIntersecting);
      }, { threshold: 0, rootMargin: '-80px 0px 100000px 0px' });
      this.observer.observe(this.kitPicker);
    }
  }

  customElements.define('kit-sticky-cta', KitStickyCTA);
}
